<template>
  <fragment>
    <v-card>
      <v-card-title class="justify-center headline pt-8 pb-6">{{ question }}</v-card-title>
      <v-card-text>
        <v-row v-for="(option, index) in shuffledOptions" :key="index">
          <v-col>
            <div
              class="quiz-btn pa-2 subtitle-1 font-weight-regular"
              :class="{
                'isCorrectAnswer': showCorrectAnswer(option),
                'isWrongAnswer': isSelectedOption(option) && !isCorrectAnswer(option),
                'fadeOut': hasOptionSelected && !isSelectedOption(option) && !isCorrectAnswer(option),
                'disabled': hasOptionSelected
              }"
              block @click="checkAnswer(option)">
              {{ option }}
              <v-icon
                v-show="hasOptionSelected && (isSelectedOption(option) || isCorrectAnswer(option))"
                color="white"
                small
                right>
                {{ (isCorrectAnswer(option)) ? 'done' : 'clear' }}
              </v-icon>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
      <v-row class="justify-end">
        <v-col class="text-right">
          <v-btn
            small
            text
            :disabled="!selectedOption || lastSlide"
            @click="nextSlide">
            {{ lastSlide ? `${$t('app.end')}` : `${$t('app.next')}` }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card>
    <Modal
      :headline="getHeadlineBasedOnPerformance()"
      :save-btn-name="$t('app.close')"
      :show-modal="showModal"
      :show-cancel-btn="false"
      @save="endQuiz">
      <div class="pb-2">{{ $t('quiz.yourScore') }} {{ score }}</div>
      <v-img :src="getGifBasedOnPerformance()" contain height="200" />
    </Modal>
  </fragment>
</template>

<script>
import Modal from '@components/modal';
import { mapGetters, mapActions } from 'vuex';
import gifs from '@/assets/gifs/quiz';
import { pathTo } from '@/plugins/router/helper';

export default {
  name: 'QuizContainer',
  components: {
    Modal,
  },
  mixins: [gifs],
  props: {
    question: {
      type: String,
      required: true,
    },
    options: {
      type: Array,
      required: true,
    },
    answer: {
      type: String,
      required: true,
    },
    score: {
      type: String,
      required: true,
    },
    performance: {
      type: Number,
      required: true,
    },
    lastSlide: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    selectedOption: '',
    success: false,
    showModal: false,
    pathTo,
  }),
  computed: {
    ...mapGetters('current', ['quizScore']),

    shuffledOptions() {
      return this._.shuffle(this.options);
    },
    hasOptionSelected() {
      return this.selectedOption.length > 0;
    },
    performanceLevel() {
      return {
        exceed: this.performance === 1,
        meet: this.performance >= 0.7 && this.performance < 1,
        needsImprovement: this.performance < 0.7,
      };
    },
  },
  methods: {
    ...mapActions('current', ['setQuizScore']),

    checkAnswer(option) {
      this.selectedOption = option;
      this.success = this.showCorrectAnswer(option);

      if (this.success) {
        this.setQuizScore(this.quizScore + 1);
      }
    },
    isSelectedOption(option) {
      return this.selectedOption === option;
    },
    isCorrectAnswer(option) {
      return option === this.answer;
    },
    showCorrectAnswer(option) {
      return (this.isSelectedOption(option) || this.hasOptionSelected) && this.isCorrectAnswer(option);
    },
    nextSlide() {
      this.selectedOption = '';
      this.$emit('next:slide');
    },
    endQuiz() {
      this.showModal = false;
      this.$router.push(pathTo.home);
    },
    getGifBasedOnPerformance() {
      if (this.performanceLevel.exceed) {
        return this.gifs.exceed[this._.random(0, this.gifs.exceed.length - 1)];
      } if (this.performanceLevel.meet) {
        return this.gifs.meet[this._.random(0, this.gifs.meet.length - 1)];
      }
      return this.gifs.needsImprovement[this._.random(0, this.gifs.needsImprovement.length - 1)];
    },
    getHeadlineBasedOnPerformance() {
      if (this.performanceLevel.exceed) {
        return this.$t('learnDialog.respect');
      }
      if (this.performanceLevel.meet) {
        return this.$t('learnDialog.goodJob');
      }
      return this.$t('learnDialog.keepLearning');
    },
  },
  watch: {
    selectedOption() {
      if (this.lastSlide && this.hasOptionSelected) {
        setTimeout(() => {
          this.showModal = true;
        }, 1000);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.quiz-btn {
  text-align: center;
  background: #d2d2d2;
  border-radius: 10px;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
}

.isCorrectAnswer {
  background-color: #4CAF50 !important;
  color: white !important;
}

.isWrongAnswer {
  background-color: #F44336 !important;
  color: white !important;
}

.fadeOut {
  opacity: 0.5;
}

.disabled {
  pointer-events: none;
}
</style>
